import {
  courseOrganizationId,
  type Course,
  type EntityDict,
  type LinkedModuleBuilds,
  type LinkedPlayerBuilds
} from '@webapp2/shared/model'
import { createOrganizationDocuments } from '@webapp2/shared/services/organization-documents'
import { state, type ModuleBuildRegistrationState } from '../state'
import { cache } from './cache'
import { ensureLinkedModuleBuilds } from './linked-module-builds-service'
import { ensureMultiplayerSessionDependencies } from './multiplayer-session-service'

const typeToStateKey = {
  prebrief: 'prebriefs',
  course: 'courses',
  courseOrganization: 'courseOrganizations',
  multiplayerSession: 'multiplayerSessions',
  userCoursePerformance: 'userCoursePerformances',
  playerTechnique: 'playerTechniques',
  assignment: 'assignments',
  userAssignmentStatus: 'userAssignmentStatuses',
  techniqueRationales: 'techniqueRationales'
} as const

const organizationDocuments = createOrganizationDocuments({
  state,
  typeToStateKey,
  organizationId: () => state.organizationId,
  cache,
  beforeMerge: async response => {
    if (response.course?.documents) {
      await loadCourseDependencies(Object.values(response.course.documents as EntityDict<Course>))
    }
  },
  removeAfterMerge: updatedTypes => (updatedTypes.includes('courseOrganization') ? removeUnreleasedCourses() : []),
  afterUpdate: ensureMultiplayerSessionDependencies
})

export const {
  initialUpdate,
  getCachedOrganizationId,
  switchCachedOrganization,
  updateEntities,
  updateByTypes,
  handleEntitiesUpdated
} = organizationDocuments

export async function setStateFromCache() {
  const [organizationId, registeredBuilds, linkedModuleBuilds, linkedPlayerBuilds] = await Promise.all([
    getCachedOrganizationId(),
    cache.getByType<ModuleBuildRegistrationState>('RegisteredState'),
    cache.getByType<LinkedModuleBuilds>('LinkedModuleBuilds'),
    cache.getByType<LinkedPlayerBuilds>('LinkedPlayerBuilds'),
    organizationDocuments.setStateFromCache()
  ])

  state.organizationId = organizationId
  state.registeredStates = registeredBuilds
  state.linkedModuleBuilds = linkedModuleBuilds
  state.linkedPlayerBuilds = linkedPlayerBuilds

  await loadCourseDependencies(Object.values(state.courses))
}

async function loadCourseDependencies(courses: Course[]) {
  await ensureLinkedModuleBuilds(courses.map(x => x.linkedModuleBuildsId))
}

function removeUnreleasedCourses(): string[] {
  const unreleasedCourseIds = Object.keys(state.courses).filter(
    x => !(courseOrganizationId(x, state.organizationId!) in state.courseOrganizations)
  )
  unreleasedCourseIds.forEach(x => delete state.courses[x])
  return unreleasedCourseIds
}
